import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion, LayoutGroup, AnimatePresence } from "framer-motion";
import PageShell from "../../components/public/PageShell";
import Pagination from "../../components/public/Pagination";
import { MagnifyingGlass, ArrowRight, Pill, Funnel, Flask, ShieldCheck, PaperclipHorizontal, GridFour, ListBullets, Rows } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import useReveal from "../../hooks/useReveal";
import usePageMeta from "../../hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const PER_PAGE = 9;
const VIEW_KEY = "bp:products:view";

export default function ProductsList() {
  usePageMeta({
    title: "Products — Bon-Heur Pharmaceuticals",
    description: "Explore 1000+ commercialised formulations from Bon-Heur Pharmaceuticals across tablets, capsules, oral liquids, dry injections and external applications.",
    keywords: "Bon-Heur products, pharmaceutical formulations, tablets, capsules, oral liquids, beta tablets, dry injections, external applications",
  });
  const [params, setParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageCount, setPageCount] = useState(1);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");
  const [page, setPage] = useState(() => Math.max(parseInt(params.get("page") || "1", 10), 1));
  const [view, setView] = useState(() => {
    if (typeof window === "undefined") return "card";
    return window.localStorage.getItem(VIEW_KEY) || "card";
  });
  const activeCat = params.get("category") || "";

  useEffect(() => {
    axios.get(`${API}/public/categories?kind=product`).then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  // Reset to page 1 whenever the filter (category / q) changes.
  useEffect(() => {
    setPage(1);
  }, [activeCat, params.get("q")]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch one page at a time — server does the slicing.
  useEffect(() => {
    setLoading(true);
    const url = new URL(`${API}/public/products`);
    url.searchParams.set("page", String(page));
    url.searchParams.set("limit", String(PER_PAGE));
    if (activeCat) url.searchParams.set("category", activeCat);
    if (params.get("q")) url.searchParams.set("q", params.get("q"));
    axios
      .get(url.toString())
      .then((r) => {
        // Server returns an envelope when ?page= is set.
        const env = r.data || {};
        setProducts(Array.isArray(env) ? env : env.items || []);
        setTotal(Array.isArray(env) ? env.length : env.total || 0);
        setPageCount(Array.isArray(env) ? 1 : env.pages || 1);
      })
      .catch(() => {
        setProducts([]);
        setTotal(0);
        setPageCount(1);
      })
      .finally(() => setLoading(false));
  }, [activeCat, params, page]);

  useEffect(() => {
    if (typeof window !== "undefined") window.localStorage.setItem(VIEW_KEY, view);
  }, [view]);

  const submitSearch = (e) => {
    e.preventDefault();
    const p = new URLSearchParams(params);
    if (q) p.set("q", q); else p.delete("q");
    setParams(p);
  };

  const setCategory = (slug) => {
    const p = new URLSearchParams(params);
    if (slug) p.set("category", slug); else p.delete("category");
    setParams(p);
  };

  return (
    <PageShell
      pageKey="products"
      eyebrow="Our portfolio"
      title="Pharmaceutical products built on science"
      subtitle="A growing portfolio of formulations developed for safety, efficacy, and real-world clinical impact."
    >
      <section className="py-14 lg:py-20 bg-white" data-testid="products-list">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Search + categories */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 mb-10">
            <form onSubmit={submitSearch} className="relative w-full lg:max-w-md" data-testid="product-search-form">
              <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search products, composition..."
                className="w-full pl-11 pr-24 py-3 rounded-full border border-[#E2E8F0] focus:border-[#C2410C] focus:outline-none focus:ring-2 focus:ring-[#C2410C]/20 text-sm transition"
                data-testid="product-search-input"
              />
              <button type="submit" className="absolute right-1.5 top-1/2 -translate-y-1/2 px-4 py-1.5 rounded-full bg-[#C2410C] hover:bg-[#9A3412] text-white text-xs font-semibold transition" data-testid="product-search-btn">
                Search
              </button>
            </form>

            <div className="flex items-center gap-3" data-testid="product-categories">
              <span className="hidden sm:inline-flex items-center gap-1.5 text-xs text-[#64748B] uppercase tracking-[0.2em] font-semibold">
                <Funnel size={12} weight="bold" /> Category
              </span>
              <Select value={activeCat || "__all__"} onValueChange={(v) => setCategory(v === "__all__" ? "" : v)}>
                <SelectTrigger
                  data-testid="product-category-select"
                  className="w-[240px] h-11 rounded-full border-[#E2E8F0] bg-white text-sm font-semibold text-[#1F2937] hover:border-[#C2410C] focus:ring-2 focus:ring-[#C2410C]/20 px-5"
                >
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="__all__" data-testid="product-cat-all">All categories</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={c.slug} data-testid={`product-cat-${c.slug}`}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="products-skeleton">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-44 rounded-2xl bg-[#F1F5F9] animate-pulse" />
              ))}
            </div>
          ) : total === 0 ? (
            <div className="py-24 text-center text-[#64748B]" data-testid="products-empty">
              <Pill size={36} className="mx-auto text-[#CBD5E1] mb-3" />
              <p>No products found. Try a different filter.</p>
            </div>
          ) : (
            <>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
                <div className="text-sm text-[#64748B]">
                  Showing <b className="text-[#1F2937]">{(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, total)}</b> of <b className="text-[#1F2937]">{total}</b> products
                </div>
                <LayoutGroup>
                <div className="inline-flex items-center gap-1 p-1 rounded-full bg-[#F1F5F9] border border-[#E2E8F0] relative" data-testid="product-view-toggle">
                  {[
                    { id: "card", label: "Cards", icon: GridFour },
                    { id: "list", label: "List", icon: ListBullets },
                    { id: "detailed", label: "Detailed", icon: Rows },
                  ].map((v) => {
                    const active = view === v.id;
                    return (
                      <button
                        key={v.id}
                        onClick={() => setView(v.id)}
                        data-testid={`view-${v.id}`}
                        className={`relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition ${active ? "text-[#C2410C]" : "text-[#475569] hover:text-[#C2410C]"}`}
                        title={v.label}
                      >
                        {active && (
                          <motion.span
                            layoutId="view-pill"
                            className="absolute inset-0 rounded-full bg-white shadow-[0_4px_14px_rgba(15,23,42,0.06)] ring-1 ring-[#0F172A]/5"
                            transition={{ type: "spring", stiffness: 380, damping: 32 }}
                          />
                        )}
                        <span className="relative flex items-center gap-1.5">
                          <v.icon size={13} weight="bold" />
                          <span className="hidden sm:inline">{v.label}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
                </LayoutGroup>
              </div>

              {(() => {
                // Server paginates now — `products` already holds the current page.
                const pageItems = products;
                const fadeSlide = {
                  initial: { opacity: 0, y: 12 },
                  animate: { opacity: 1, y: 0 },
                  exit:    { opacity: 0, y: -8 },
                  transition: { duration: 0.35, ease: [0.65, 0, 0.35, 1] },
                };
                if (view === "list") {
                  return (
                    <AnimatePresence mode="wait">
                      <motion.div key="list" {...fadeSlide} className="divide-y divide-[#E2E8F0] rounded-2xl border border-[#E2E8F0] bg-white overflow-hidden" data-testid="products-grid">
                        {pageItems.map((p, i) => <ProductRow key={p.id} p={p} i={i} />)}
                      </motion.div>
                    </AnimatePresence>
                  );
                }
                if (view === "detailed") {
                  return (
                    <AnimatePresence mode="wait">
                      <motion.div key="detailed" {...fadeSlide} className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="products-grid">
                        {pageItems.map((p, i) => <ProductDetailed key={p.id} p={p} i={i} />)}
                      </motion.div>
                    </AnimatePresence>
                  );
                }
                return (
                  <AnimatePresence mode="wait">
                    <motion.div key="card" {...fadeSlide} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="products-grid">
                      {pageItems.map((p, i) => <ProductCard key={p.id} p={p} i={i} />)}
                    </motion.div>
                  </AnimatePresence>
                );
              })()}

              <Pagination
                page={page}
                pageCount={pageCount}
                onChange={(p) => { setPage(p); window.scrollTo({ top: 200, behavior: "smooth" }); }}
                testIdPrefix="products-pagination"
              />
            </>
          )}
        </div>
      </section>
    </PageShell>
  );
}

// Stable, accessible color tokens for each therapeutic area / category.
// Maps category slug → { accent, soft, label }.
const CATEGORY_COLORS = {
  "antibiotics":           { accent: "#C2410C", soft: "rgba(194,65,12,0.10)", label: "Antibiotics" },
  "cardiology":            { accent: "#DC2626", soft: "rgba(220,38,38,0.10)",  label: "Cardiology" },
  "diabetology":           { accent: "#7C3AED", soft: "rgba(124,58,237,0.10)", label: "Diabetology" },
  "pain-inflammation":     { accent: "#0EA5E9", soft: "rgba(14,165,233,0.10)", label: "Pain & Inflammation" },
  "gastroenterology":      { accent: "#16A34A", soft: "rgba(22,163,74,0.10)",  label: "Gastroenterology" },
};
function colorFor(slug = "") {
  return CATEGORY_COLORS[slug] || { accent: "#475569", soft: "rgba(71,85,105,0.10)", label: slug };
}

// Compact label-style "pill" used as inline meta info
function MetaPill({ icon: Icon, label, accent }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/70 backdrop-blur-sm border border-[#E2E8F0] text-[10px] tracking-[0.05em] uppercase font-semibold text-[#475569]">
      {Icon && <Icon size={11} weight="bold" style={{ color: accent }} />} {label}
    </span>
  );
}

function ProductCard({ p, i }) {
  const { ref, visible } = useReveal();
  const catSlug = p.category?.slug || p.category_slug || (p.category_name || "").toLowerCase().replace(/\s+/g, "-").replace(/&/g, "");
  const cat = colorFor(catSlug);
  const initials = p.name?.split(/[\s-]/).map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "RX";
  const hasDocs = !!(p.stability_report_url || p.dcgi_approval_url);

  return (
    <div
      ref={ref}
      data-testid={`product-card-${p.slug}`}
      className="transition-all duration-700"
      style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(20px)", transitionDelay: `${(i % 9) * 70}ms` }}
    >
      <Link
        to={`/products/${p.slug}`}
        className="group relative block overflow-hidden rounded-2xl bg-white border border-[#E2E8F0] hover:border-[#C2410C] hover:shadow-[0_18px_40px_-18px_rgba(15,23,42,0.18)] transition-all duration-300"
      >
        {/* Top accent bar — therapeutic area colour */}
        <div className="h-1.5 w-full" style={{ background: `linear-gradient(90deg, ${cat.accent} 0%, ${cat.accent}99 100%)` }} aria-hidden />

        <div className="p-5 sm:p-6 grid grid-cols-[auto_1fr] gap-4 sm:gap-5">
          {/* Initials block — fills the visual gap left by removing the image */}
          <div
            className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl flex flex-col items-center justify-center font-bold leading-none transition-transform duration-300 group-hover:-translate-y-0.5"
            style={{ background: cat.soft, color: cat.accent, fontFamily: "'Manrope', sans-serif" }}
          >
            <span className="text-xl sm:text-2xl tracking-tight">{initials}</span>
            <span className="mt-1 text-[8px] tracking-[0.15em] uppercase opacity-70">{p.dosage_form?.split(" ")[0] || "RX"}</span>
          </div>

          {/* Main content */}
          <div className="min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="text-[10px] tracking-[0.25em] uppercase font-semibold" style={{ color: cat.accent }}>
                {p.category_name || cat.label}
              </div>
              {p.featured && (
                <span className="px-2 py-0.5 rounded-full bg-[#FB923C]/10 text-[#C2410C] text-[9px] tracking-[0.2em] uppercase font-semibold flex-shrink-0">Featured</span>
              )}
            </div>
            <h3
              className="mt-1.5 text-lg sm:text-xl font-bold text-[#0F172A] group-hover:text-[#C2410C] transition-colors leading-tight line-clamp-2"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              {p.name}
            </h3>
            {p.composition && (
              <p className="mt-1.5 text-xs sm:text-sm text-[#475569] leading-snug line-clamp-2 italic">
                {p.composition}
              </p>
            )}

            {/* Meta strip */}
            <div className="mt-3 flex flex-wrap gap-1.5">
              {p.dosage_form && <MetaPill icon={Pill} accent={cat.accent} label={p.dosage_form} />}
              {p.pack_size && <MetaPill icon={Flask} accent={cat.accent} label={p.pack_size} />}
              {p.prescription_status && (
                <MetaPill icon={ShieldCheck} accent={p.prescription_status === "rx" ? "#DC2626" : "#16A34A"} label={p.prescription_status?.toUpperCase()} />
              )}
              {hasDocs && <MetaPill icon={PaperclipHorizontal} accent="#0EA5E9" label="Dossier" />}
            </div>

            <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-[#C2410C] group-hover:gap-2.5 transition-all">
              View details <ArrowRight size={12} weight="bold" />
            </div>
          </div>
        </div>

        {/* Soft hover backdrop accent */}
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-12 -right-12 w-48 h-48 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{ background: `radial-gradient(circle, ${cat.soft} 0%, transparent 70%)` }}
        />
      </Link>
    </div>
  );
}


// ── Compact LIST view ────────────────────────────────────────────────────────
function ProductRow({ p, i }) {
  const { ref, visible } = useReveal();
  const catSlug = p.category?.slug || (p.category_name || "").toLowerCase().replace(/\s+/g, "-").replace(/&/g, "");
  const cat = colorFor(catSlug);
  const hasDocs = !!(p.stability_report_url || p.dcgi_approval_url);
  return (
    <Link
      ref={ref}
      to={`/products/${p.slug}`}
      data-testid={`product-card-${p.slug}`}
      className="group flex items-center gap-4 px-5 py-4 hover:bg-[#F8FAFC] transition-colors"
      style={{ opacity: visible ? 1 : 0, transition: "opacity 500ms", transitionDelay: `${(i % 9) * 40}ms` }}
    >
      <div className="w-1.5 h-10 rounded-full flex-shrink-0" style={{ background: cat.accent }} aria-hidden />
      <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-[1.4fr_1.6fr_auto] gap-3 sm:gap-5 items-center">
        <div className="min-w-0">
          <div className="text-[10px] tracking-[0.25em] uppercase font-semibold" style={{ color: cat.accent }}>{p.category_name || cat.label}</div>
          <div className="mt-0.5 text-base font-bold text-[#0F172A] group-hover:text-[#C2410C] transition-colors truncate" style={{ fontFamily: "'Manrope', sans-serif" }}>{p.name}</div>
        </div>
        <div className="text-xs text-[#475569] truncate italic">{p.composition || "—"}</div>
        <div className="flex flex-wrap gap-1.5 justify-end">
          {p.dosage_form && <MetaPill icon={Pill} accent={cat.accent} label={p.dosage_form} />}
          {p.prescription_status && <MetaPill icon={ShieldCheck} accent={p.prescription_status === "rx" ? "#DC2626" : "#16A34A"} label={p.prescription_status.toUpperCase()} />}
          {hasDocs && <MetaPill icon={PaperclipHorizontal} accent="#0EA5E9" label="Dossier" />}
        </div>
      </div>
      <ArrowRight size={14} weight="bold" className="text-[#C2410C] flex-shrink-0 transition-transform group-hover:translate-x-1" />
    </Link>
  );
}


// ── Detailed view (2-col, expanded composition + indication) ────────────────
function ProductDetailed({ p, i }) {
  const { ref, visible } = useReveal();
  const catSlug = p.category?.slug || (p.category_name || "").toLowerCase().replace(/\s+/g, "-").replace(/&/g, "");
  const cat = colorFor(catSlug);
  const hasDocs = !!(p.stability_report_url || p.dcgi_approval_url);
  return (
    <Link
      ref={ref}
      to={`/products/${p.slug}`}
      data-testid={`product-card-${p.slug}`}
      className="group relative block overflow-hidden rounded-2xl bg-white border border-[#E2E8F0] hover:border-[#C2410C] hover:shadow-[0_18px_40px_-18px_rgba(15,23,42,0.18)] transition-all duration-300"
      style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(16px)", transition: "opacity 600ms, transform 600ms, border-color 300ms, box-shadow 300ms", transitionDelay: `${(i % 6) * 80}ms` }}
    >
      <div className="h-1.5 w-full" style={{ background: cat.accent }} aria-hidden />
      <div className="p-6 sm:p-7">
        <div className="flex items-start justify-between gap-3">
          <div className="text-[10px] tracking-[0.25em] uppercase font-semibold" style={{ color: cat.accent }}>{p.category_name || cat.label}</div>
          {p.featured && <span className="px-2 py-0.5 rounded-full bg-[#FB923C]/10 text-[#C2410C] text-[9px] tracking-[0.2em] uppercase font-semibold">Featured</span>}
        </div>
        <h3 className="mt-2 text-2xl font-bold text-[#0F172A] group-hover:text-[#C2410C] transition-colors leading-tight" style={{ fontFamily: "'Manrope', sans-serif" }}>{p.name}</h3>
        {p.composition && <p className="mt-2 text-sm text-[#475569] leading-snug italic">{p.composition}</p>}
        {p.short_description && <p className="mt-4 text-sm text-[#374151] leading-relaxed line-clamp-3">{p.short_description}</p>}
        <div className="mt-5 grid grid-cols-2 gap-2 text-[11px]">
          {p.dosage_form && <DataRow label="Form" value={p.dosage_form} />}
          {p.pack_size && <DataRow label="Pack" value={p.pack_size} />}
          {p.therapeutic_area && <DataRow label="Therapy" value={p.therapeutic_area} />}
          {p.prescription_status && <DataRow label="Status" value={p.prescription_status.toUpperCase()} />}
        </div>
        <div className="mt-5 flex items-center justify-between">
          <div className="flex flex-wrap gap-1.5">
            {hasDocs && <MetaPill icon={PaperclipHorizontal} accent="#0EA5E9" label="Dossier available" />}
          </div>
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#C2410C] group-hover:gap-2 transition-all">View details <ArrowRight size={12} weight="bold" /></span>
        </div>
      </div>
    </Link>
  );
}

function DataRow({ label, value }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-[#94A3B8] uppercase tracking-wider w-12 flex-shrink-0">{label}</span>
      <span className="text-[#1F2937] font-semibold truncate">{value}</span>
    </div>
  );
}
